import { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import api from "@/lib/api";
import { PriceDisplay } from "@/components/PriceDisplay";
import { useAuth } from "@/contexts/AuthContext";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { BuyNowPanel } from "@/components/shop/BuyNowPanel";
import { QuoteRequestForm } from "@/components/shop/QuoteRequestForm";
import { PlayCircle, Images, X, ArrowLeft, ArrowRight } from "@phosphor-icons/react";

function getImages(item) {
  if (Array.isArray(item.images) && item.images.length > 0) return item.images;
  if (item.image_url) return [item.image_url];
  return [];
}

function youtubeEmbedUrl(url) {
  if (!url) return null;
  if (url.includes("/embed/")) return url;
  let id = null;
  const youtuBeMatch = url.match(/youtu\.be\/([\w-]+)/);
  if (youtuBeMatch) id = youtuBeMatch[1];
  const watchMatch = url.match(/[?&]v=([\w-]+)/);
  if (!id && watchMatch) id = watchMatch[1];
  return id ? `https://www.youtube.com/embed/${id}` : null;
}

const isBuyable = (item) => (item.purchase_mode || "buy") === "buy" && item.in_stock;

function ProductDetailModal({ item, onClose }) {
  const images = getImages(item);
  const [activeIdx, setActiveIdx] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const embed = youtubeEmbedUrl(item.youtube_url);

  const next = () => setActiveIdx((i) => (i + 1) % Math.max(images.length, 1));
  const prev = () => setActiveIdx((i) => (i - 1 + Math.max(images.length, 1)) % Math.max(images.length, 1));

  return (
    <div className="fixed inset-0 z-[60] bg-black/90 overflow-y-auto" onClick={onClose} data-testid="product-detail-modal">
      <div className="max-w-5xl mx-auto my-8 px-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="font-mono text-[10px] text-[#00D4FF] tracking-widest uppercase">{item.category} · SKU {item.sku || "—"}</div>
          <button onClick={onClose} className="btn-secondary text-xs py-2 px-3" data-testid="product-detail-close">
            <X size={14} weight="bold" /> CLOSE
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7">
            {showVideo && embed ? (
              <div className="aspect-video bg-black border border-[#262626] relative">
                <iframe src={embed} className="w-full h-full" title={item.name} allowFullScreen />
                <button onClick={() => setShowVideo(false)} className="absolute top-2 right-2 bg-[#0A0A0A] border border-[#262626] text-white p-2" data-testid="close-product-video">
                  <X size={14} weight="bold" />
                </button>
              </div>
            ) : (
              <div className="aspect-video bg-[#0A0A0A] border border-[#262626] overflow-hidden relative">
                {images.length > 0 ? (
                  <img src={images[activeIdx]} alt={item.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center font-mono text-xs text-[#52525B]">NO IMAGE</div>
                )}
                {images.length > 1 && (
                  <>
                    <button onClick={prev} className="absolute left-2 top-1/2 -translate-y-1/2 bg-[#0A0A0A]/80 border border-[#262626] text-white p-2 hover:border-[#00D4FF]" data-testid="product-gallery-prev">
                      <ArrowLeft size={14} weight="bold" />
                    </button>
                    <button onClick={next} className="absolute right-2 top-1/2 -translate-y-1/2 bg-[#0A0A0A]/80 border border-[#262626] text-white p-2 hover:border-[#00D4FF]" data-testid="product-gallery-next">
                      <ArrowRight size={14} weight="bold" />
                    </button>
                    <div className="absolute bottom-2 right-2 bg-[#0A0A0A]/80 border border-[#262626] font-mono text-[10px] text-white tracking-widest px-2 py-1">
                      {activeIdx + 1} / {images.length}
                    </div>
                  </>
                )}
                {embed && (
                  <button onClick={() => setShowVideo(true)} className="absolute top-2 left-2 bg-[#00D4FF] text-black px-3 py-1.5 font-chivo font-bold text-xs uppercase tracking-wider hover:bg-[#33DDFF]" data-testid="play-product-video">
                    <PlayCircle size={14} weight="fill" /> PLAY VIDEO
                  </button>
                )}
              </div>
            )}

            {images.length > 1 && !showVideo && (
              <div className="grid grid-cols-5 sm:grid-cols-6 gap-2 mt-2">
                {images.map((url, idx) => (
                  <button key={url + idx} onClick={() => setActiveIdx(idx)} className={`aspect-square overflow-hidden border ${idx === activeIdx ? "border-[#00D4FF]" : "border-[#262626] hover:border-[#404040]"}`} data-testid={`product-gallery-thumb-${idx}`}>
                    <img src={url} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-5 surface p-6">
            <h1 className="font-chivo font-black text-2xl text-white uppercase tracking-tight mb-3">{item.name}</h1>
            <div className="flex flex-wrap gap-2 mb-4">
              <span className={`tag ${item.in_stock ? "tag-active" : ""}`}>{item.in_stock ? "IN STOCK" : "BACKORDER"}</span>
            </div>
            <p className="font-plex text-sm text-[#9CA3AF] leading-relaxed mb-6 whitespace-pre-wrap">{item.description}</p>
            <div className="border-t border-[#262626] pt-4 flex items-end justify-between gap-4">
              <span className={`tag ${isBuyable(item) ? "tag-active" : ""}`} data-testid={`product-detail-mode-${item.id}`}>{isBuyable(item) ? "BUY NOW" : "REQUEST A QUOTE"}</span>
              <PriceDisplay msrp={item.msrp_price} member={item.member_price} showMember={item.show_member_price} testId={`product-detail-${item.id}-price`} />
            </div>
            {isBuyable(item) ? <BuyNowPanel item={item} /> : <QuoteRequestForm item={item} />}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [activeCat, setActiveCat] = useState("All");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const { isMember } = useAuth();
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    if (params.get("checkout") === "cancelled") {
      toast("Checkout cancelled — nothing was charged.", { id: "checkout-cancelled" });
      setParams({}, { replace: true });
    }
  }, [params, setParams]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/products");
        setProducts(data);
      } catch (err) {
        console.error("Failed to load products:", err);
      }
      setLoading(false);
    })();
  }, []);

  const categories = ["All", ...Array.from(new Set(products.map((p) => p.category)))];
  const filtered = activeCat === "All" ? products : products.filter((p) => p.category === activeCat);

  return (
    <Layout>
      {selected && <ProductDetailModal item={selected} onClose={() => setSelected(null)} />}
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8 items-end">
          <div className="lg:col-span-8">
            <div className="section-label mb-3">— EQUIPMENT CATALOG</div>
            <h1 className="font-chivo font-black text-4xl lg:text-5xl text-white uppercase tracking-tight" data-testid="shop-headline">Machines & Accessories</h1>
            <p className="font-plex text-[#9CA3AF] mt-3 max-w-2xl">
              {isMember
                ? "You're seeing live member pricing. Lock in your hardware now."
                : "Public visitors see MSRP. Members unlock preferred pricing on every product."}
            </p>
          </div>
          {!isMember && (
            <div className="lg:col-span-4 surface p-4 flex items-center justify-between gap-4">
              <div>
                <div className="font-mono text-xs text-[#00D4FF] tracking-widest uppercase">UNLOCK MEMBER PRICES</div>
                <div className="font-plex text-xs text-[#9CA3AF] mt-1">Save on every product · cancel anytime</div>
              </div>
              <Link to="/pricing" className="btn-primary text-xs py-2 px-4" data-testid="shop-join-cta">Join</Link>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {categories.map((c) => (
            <button key={c} onClick={() => setActiveCat(c)} className={`tag ${activeCat === c ? "tag-active" : ""}`} data-testid={`product-category-${c.toLowerCase().replace(/\s+/g, "-")}`}>
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="font-mono text-xs text-[#52525B]">[LOADING]</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#262626] border border-[#262626]" data-testid="products-grid">
            {filtered.map((p) => {
              const productImages = getImages(p);
              const primary = productImages[0];
              const hasVideo = !!youtubeEmbedUrl(p.youtube_url);
              return (
                <button key={p.id} onClick={() => setSelected(p)} className="bg-[#0A0A0A] hover:bg-[#121212] transition-colors text-left" data-testid={`product-card-${p.id}`}>
                  <div className="aspect-video bg-[#0A0A0A] overflow-hidden border-b border-[#262626] relative">
                    {primary && <img src={primary} alt={p.name} className="w-full h-full object-cover" />}
                    <div className="absolute bottom-3 right-3 flex gap-1">
                      {productImages.length > 1 && (
                        <span className="tag bg-[#0A0A0A]/90"><Images size={10} weight="bold" /> {productImages.length}</span>
                      )}
                      {hasVideo && (
                        <span className="tag bg-[#0A0A0A]/90 tag-active"><PlayCircle size={10} weight="fill" /> VIDEO</span>
                      )}
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div>
                        <div className="font-mono text-[10px] text-[#00D4FF] tracking-widest uppercase">{p.category}</div>
                        <h3 className="font-chivo font-bold text-lg text-white mt-1">{p.name}</h3>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className={`tag ${p.in_stock ? "tag-active" : ""}`}>{p.in_stock ? "IN STOCK" : "BACKORDER"}</span>
                        <span className="tag" data-testid={`product-mode-${p.id}`}>{isBuyable(p) ? "BUY NOW" : "QUOTE"}</span>
                      </div>
                    </div>
                    <p className="font-plex text-sm text-[#9CA3AF] line-clamp-2 mb-4">{p.description}</p>
                    <div className="flex items-end justify-between border-t border-[#262626] pt-4">
                      <div className="font-mono text-[10px] text-[#52525B] tracking-widest">SKU {p.sku || "—"}</div>
                      <PriceDisplay msrp={p.msrp_price} member={p.member_price} showMember={p.show_member_price} testId={`product-${p.id}-price`} />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
